import { useEffect, useState } from "react";
import { User, UserCircle, MapPin, Palette, RefreshCw, Eye, EyeOff, ShieldCheck, Lock, Pencil, Save, X, Mail, CreditCard } from "lucide-react";
import toast from "react-hot-toast";
import { authFetch, getApiUrl, SessionExpiredError } from "../../../utils/api";
import FileUpload from "../../../pages/user/branding-file-upload";
import AccountSwitcherModal from "../../ui/AccountSwitcherModal";
import AgreementSection from "../../agreement-section/agreement-section.component";
import { isClientOnlyForDistro } from "../../../utils/firmAccess";

const InfoRow = ({ label, value, sensitive = false }) => {
    const [isVisible, setIsVisible] = useState(false);

    return (
        <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 w-32 flex-shrink-0">{label}</span>
            {sensitive ? (
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-900 font-medium font-mono">
                        {isVisible ? value : (value ? value.replace(/\d/g, '•') : null)}
                    </span>
                    {value && (
                        <button
                            onClick={() => setIsVisible(!isVisible)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                            aria-label={isVisible ? "Hide value" : "Show value"}
                        >
                            {isVisible ? (
                                <EyeOff className="w-3.5 h-3.5 text-gray-400" />
                            ) : (
                                <Eye className="w-3.5 h-3.5 text-gray-400" />
                            )}
                        </button>
                    )}
                </div>
            ) : (
                <span className="text-sm text-gray-900 font-medium truncate">
                    {value || <span className="text-gray-400 font-normal">Not provided</span>}
                </span>
            )}
        </div>
    );
};

const EditableField = ({ label, name, value, onChange, error, locked, lockedHint, type = "text", maxLength, inputMode, hint }) => (
    <div className="flex items-start gap-3">
        <label className="text-sm text-gray-500 w-32 flex-shrink-0 mt-2 flex items-center gap-1.5">
            {label}
            {locked && <Lock className="w-3 h-3 text-gray-400" />}
        </label>
        <div className="flex-1 min-w-0">
            <input
                type={type}
                name={name}
                value={value ?? ""}
                onChange={onChange}
                disabled={locked}
                maxLength={maxLength}
                inputMode={inputMode}
                title={locked ? lockedHint : undefined}
                className={`w-full px-3 py-1.5 text-sm rounded-md border transition-colors ${
                    error
                        ? "border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-200"
                        : "border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary/20"
                } ${locked ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-white"}`}
            />
            {error ? (
                <p className="text-xs text-red-500 mt-0.5">{error}</p>
            ) : hint ? (
                <p className="text-xs text-gray-400 mt-0.5">{hint}</p>
            ) : null}
        </div>
    </div>
);

const Toggle = ({ enabled, onChange, disabled }) => (
    <button
        type="button"
        role="switch"
        aria-checked={enabled}
        disabled={disabled}
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50 disabled:cursor-not-allowed ${
            enabled ? "bg-primary" : "bg-gray-200"
        }`}
    >
        <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                enabled ? "translate-x-5" : "translate-x-0"
            }`}
        />
    </button>
);

const CardHeader = ({ icon: Icon, title, cardKey, isEditing, canEdit, lockOthers, saving, onEdit, onSave, onCancel, extra }) => (
    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                <Icon className="w-5 h-5 text-primary" />
            </div>
            <h3 className="font-semibold text-gray-900 truncate">{title}</h3>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
            {extra}
            {canEdit && (
                isEditing ? (
                    <>
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={saving}
                            style={{ fontSize: '12px', fontWeight: 500, lineHeight: 1 }}
                            className="box-border inline-flex items-center justify-center gap-1 h-8 min-w-[80px] px-3 text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            <X className="w-3.5 h-3.5" />
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={onSave}
                            disabled={saving}
                            style={{ fontSize: '12px', fontWeight: 500, lineHeight: 1 }}
                            className="box-border inline-flex items-center justify-center gap-1 h-8 min-w-[80px] px-3 text-white bg-primary border border-primary rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50"
                        >
                            <Save className="w-3.5 h-3.5" />
                            {saving ? "Saving..." : "Save"}
                        </button>
                    </>
                ) : (
                    <button
                        onClick={onEdit}
                        disabled={lockOthers}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-primary hover:bg-primary/5 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-600"
                        title={lockOthers ? "Finish editing the other card first" : "Edit"}
                    >
                        <Pencil className="w-3.5 h-3.5" />
                        Edit
                    </button>
                )
            )}
        </div>
    </div>
);

const EDITABLE_FIELDS = [
    "mailingStreet1", "mailingStreet2", "mailingCity", "mailingProv", "mailingPostalCode",
    "phone",
    "contactPerson",
    "bankTransitNo", "bankNo", "bankAccountNo",
];

const buildFormState = (info) => ({
    mailingStreet1: info?.mailingStreet1 ?? "",
    mailingStreet2: info?.mailingStreet2 ?? "",
    mailingCity: info?.mailingCity ?? "",
    mailingProv: info?.mailingProv ?? "",
    mailingPostalCode: info?.mailingPostalCode ?? "",
    phone: info?.phone ?? "",
    contactPerson: info?.contactPerson ?? "",
    bankTransitNo: info?.bankTransitNo ?? "",
    bankNo: info?.bankNo ?? "",
    bankAccountNo: info?.bankAccountNo ?? "",
});

// fields each card owns (used for per-card error filtering)
const CARD_FIELDS = {
    account: ["bankTransitNo", "bankNo", "bankAccountNo"],
    contact: ["contactPerson", "phone", "mailingStreet1", "mailingStreet2", "mailingCity", "mailingProv", "mailingPostalCode"],
};

// digit-only fields with max length (variable up to max)
const BANK_FIELDS = {
    bankTransitNo: { maxLength: 5, label: "Transit No.", confirmKey: "bankTransitNoConfirm" },
    bankNo: { maxLength: 3, label: "Bank No.", confirmKey: "bankNoConfirm" },
    bankAccountNo: { maxLength: 12, label: "Account No.", confirmKey: "bankAccountNoConfirm" },
};

const buildBankConfirmState = () => ({
    bankTransitNoConfirm: "",
    bankNoConfirm: "",
    bankAccountNoConfirm: "",
});

const AccountInfoOwner = ({ info, currentUser, setCurrentUser, onSessionExpired, onTokenRefresh, onProfileUpdated }) => {
    const [showSwitchModal, setShowSwitchModal] = useState(false);
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(info?.twoFactorEnabled ?? currentUser?.twoFactorEnabled ?? false);
    const [twoFactorLoading, setTwoFactorLoading] = useState(false);
    const [twoFactorMessage, setTwoFactorMessage] = useState(null);

    const [editingCard, setEditingCard] = useState(null); // 'account' | 'contact' | 'banking' | 'address' | null
    const [form, setForm] = useState(() => buildFormState(info));
    const [bankConfirm, setBankConfirm] = useState(buildBankConfirmState);
    const [fieldErrors, setFieldErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const apiUrl = getApiUrl();
    const authParams = { currentUser, onSessionExpired, onTokenRefresh };
    const clientOnly = isClientOnlyForDistro(currentUser, info?.id);
    const canEdit = !clientOnly && (currentUser?.role === "owner" || currentUser?.role === "dual");

    useEffect(() => {
        // resync form whenever the info prop changes (e.g., after distro switch)
        setForm(buildFormState(info));
        setBankConfirm(buildBankConfirmState());
        setFieldErrors({});
        setEditingCard(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [info?.id]);

    const handleToggle2FA = async (newValue) => {
        setTwoFactorLoading(true);
        setTwoFactorMessage(null);
        try {
            const response = await fetch(`${apiUrl}/users/twofactor/${currentUser.id}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${currentUser.token}`,
                },
                body: JSON.stringify({ enabled: newValue }),
            });

            if (!response.ok) {
                const data = await response.json();
                setTwoFactorMessage({ text: data.message || "Failed to update setting.", error: true });
                return;
            }

            setTwoFactorEnabled(newValue);
            setCurrentUser({ ...currentUser, twoFactorEnabled: newValue });
            setTwoFactorMessage({
                text: newValue
                    ? "Two-factor authentication enabled."
                    : "Two-factor authentication disabled.",
                error: false,
            });
        } catch {
            setTwoFactorMessage({ text: "An error occurred. Please try again.", error: true });
        } finally {
            setTwoFactorLoading(false);
        }
    };

    const handleEdit = (cardKey) => {
        setForm(buildFormState(info));
        setBankConfirm(buildBankConfirmState());
        setFieldErrors({});
        setEditingCard(cardKey);
    };

    const handleCancel = () => {
        setForm(buildFormState(info));
        setBankConfirm(buildBankConfirmState());
        setFieldErrors({});
        setEditingCard(null);
    };

    const clearFieldError = (name) => {
        if (!fieldErrors[name]) return;
        setFieldErrors((prev) => {
            const next = { ...prev };
            delete next[name];
            return next;
        });
    };

    const handleChange = (e) => {
        const { name } = e.target;
        let { value } = e.target;
        // strip non-digits and clamp at max length for bank fields
        if (BANK_FIELDS[name]) {
            value = value.replace(/\D/g, "").slice(0, BANK_FIELDS[name].maxLength);
        }
        setForm((prev) => ({ ...prev, [name]: value }));
        clearFieldError(name);
        // changing a banking source field invalidates its confirm
        const bankField = BANK_FIELDS[name];
        if (bankField) clearFieldError(bankField.confirmKey);
    };

    const handleBankConfirmChange = (e) => {
        const { name } = e.target;
        // confirm fields mirror the source field's digit-only / maxLength rules
        const source = Object.values(BANK_FIELDS).find((f) => f.confirmKey === name);
        const max = source?.maxLength ?? 12;
        const value = e.target.value.replace(/\D/g, "").slice(0, max);
        setBankConfirm((prev) => ({ ...prev, [name]: value }));
        clearFieldError(name);
    };

    const handleSave = async () => {
        if (saving) return;

        // require double-entry confirmation for any banking field that's been changed
        const confirmErrors = {};
        Object.entries(BANK_FIELDS).forEach(([fieldName, cfg]) => {
            const original = info?.[fieldName] ?? "";
            if (form[fieldName] !== original && bankConfirm[cfg.confirmKey] !== form[fieldName]) {
                confirmErrors[cfg.confirmKey] = `Doesn't match ${cfg.label}.`;
            }
        });
        if (Object.keys(confirmErrors).length > 0) {
            setFieldErrors(confirmErrors);
            toast.error("Please confirm the updated banking details.");
            return;
        }

        setSaving(true);
        setFieldErrors({});
        try {
            const payload = { distroAccountId: info.id };
            EDITABLE_FIELDS.forEach((f) => { payload[f] = form[f]; });

            const response = await authFetch(
                `${apiUrl}/distroaccount/update-profile`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                },
                authParams
            );

            if (response.ok) {
                toast.success("Profile updated.");
                setEditingCard(null);
                if (onProfileUpdated) onProfileUpdated();
                return;
            }

            if (response.status === 400) {
                const data = await response.json().catch(() => ({}));
                if (data?.fieldErrors) {
                    setFieldErrors(data.fieldErrors);
                    toast.error("Please fix the highlighted fields.");
                } else {
                    toast.error(data?.message || "Validation failed.");
                }
                return;
            }

            if (response.status === 403) {
                toast.error("You don't have permission to edit this distro.");
                return;
            }

            toast.error("Save failed, please try again.");
        } catch (err) {
            if (!(err instanceof SessionExpiredError)) {
                console.error("Profile save failed:", err);
                toast.error("Save failed, please try again.");
            }
        } finally {
            setSaving(false);
        }
    };

    const renderCardHeader = (cardKey, icon, title, extra) => (
        <CardHeader
            icon={icon}
            title={title}
            cardKey={cardKey}
            isEditing={editingCard === cardKey}
            canEdit={canEdit}
            lockOthers={editingCard !== null && editingCard !== cardKey}
            saving={saving}
            onEdit={() => handleEdit(cardKey)}
            onSave={handleSave}
            onCancel={handleCancel}
            extra={extra}
        />
    );

    return (
        <div className="flex-1 px-6 md:px-8 py-6 pb-5">
            {/* page header */}
            <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-primary/10 rounded-xl">
                    <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Profile</h1>
                    <p className="text-sm text-gray-500">
                        {info?.name ? `Manage account settings for ${info.name}` : 'Manage account settings'}
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Account Information */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
                    {renderCardHeader('account', UserCircle, 'Firm Information',
                        currentUser?.distros?.length > 1 && editingCard !== 'account' && (
                            <button
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-primary hover:bg-primary/5 rounded-md transition-colors"
                                onClick={() => setShowSwitchModal(true)}
                                type="button"
                            >
                                <RefreshCw className="w-3.5 h-3.5" />
                                Switch
                            </button>
                        )
                    )}
                    <div className="p-5 space-y-3">
                        <InfoRow label="Name" value={info.name} />
                        <InfoRow label="Description" value={info.description} />
                        <InfoRow label="Account ID" value={info.accountId} />
                        <InfoRow label="Distro ID" value={info.distroId} />

                        {!clientOnly && (
                            <div className="pt-3 mt-3 border-t border-gray-100">
                                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                    <CreditCard className="w-3 h-3" />
                                    Banking
                                </h4>
                                <div className="space-y-3">
                                    {editingCard === 'account' ? (
                                        <>
                                            <EditableField label="Transit No." name="bankTransitNo" value={form.bankTransitNo} onChange={handleChange} error={fieldErrors.bankTransitNo} maxLength={5} inputMode="numeric" hint="Up to 5 digits" />
                                            {form.bankTransitNo !== (info.bankTransitNo ?? "") && (
                                                <EditableField label="Confirm Transit No." name="bankTransitNoConfirm" value={bankConfirm.bankTransitNoConfirm} onChange={handleBankConfirmChange} error={fieldErrors.bankTransitNoConfirm} maxLength={5} inputMode="numeric" hint="Re-enter to confirm change" />
                                            )}
                                            <EditableField label="Bank No." name="bankNo" value={form.bankNo} onChange={handleChange} error={fieldErrors.bankNo} maxLength={3} inputMode="numeric" hint="Up to 3 digits" />
                                            {form.bankNo !== (info.bankNo ?? "") && (
                                                <EditableField label="Confirm Bank No." name="bankNoConfirm" value={bankConfirm.bankNoConfirm} onChange={handleBankConfirmChange} error={fieldErrors.bankNoConfirm} maxLength={3} inputMode="numeric" hint="Re-enter to confirm change" />
                                            )}
                                            <EditableField label="Account No." name="bankAccountNo" value={form.bankAccountNo} onChange={handleChange} error={fieldErrors.bankAccountNo} maxLength={12} inputMode="numeric" hint="Up to 12 digits" />
                                            {form.bankAccountNo !== (info.bankAccountNo ?? "") && (
                                                <EditableField label="Confirm Account No." name="bankAccountNoConfirm" value={bankConfirm.bankAccountNoConfirm} onChange={handleBankConfirmChange} error={fieldErrors.bankAccountNoConfirm} maxLength={12} inputMode="numeric" hint="Re-enter to confirm change" />
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <InfoRow label="Transit No." value={info.bankTransitNo} sensitive />
                                            <InfoRow label="Bank No." value={info.bankNo} sensitive />
                                            <InfoRow label="Account No." value={info.bankAccountNo} sensitive />
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {(!currentUser?.opsActor || currentUser.opsScopeKind === 'user') && (
                        <div className="pt-3 mt-3 border-t border-gray-100">
                            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Security</h4>
                            <div className="flex items-start justify-between gap-4 px-1 py-1">
                                <div className="flex items-start gap-2 min-w-0">
                                    <ShieldCheck className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900">Two-factor authentication</p>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            Verify your identity via email on unrecognized devices.
                                        </p>
                                        {twoFactorMessage && (
                                            <p className={`text-xs mt-1.5 font-medium ${twoFactorMessage.error ? "text-red-500" : "text-green-600"}`}>
                                                {twoFactorMessage.text}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <Toggle
                                    enabled={twoFactorEnabled}
                                    onChange={handleToggle2FA}
                                    disabled={twoFactorLoading || !!currentUser?.opsActor}
                                />
                            </div>
                        </div>
                        )}
                    </div>
                </div>

                {/* Contact & Address */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
                    {renderCardHeader('contact', Mail, 'Contact & Address')}
                    <div className="p-5 space-y-3">
                        <InfoRow label="Your Login Email" value={info.email} />
                        <InfoRow label="Contact Email" value={info.contactEmail} />
                        {editingCard === 'contact' ? (
                            <>
                                <EditableField label="Contact Person" name="contactPerson" value={form.contactPerson} onChange={handleChange} error={fieldErrors.contactPerson} />
                                <EditableField label="Phone" name="phone" value={form.phone} onChange={handleChange} error={fieldErrors.phone} />
                            </>
                        ) : (
                            <>
                                <InfoRow label="Contact Person" value={info.contactPerson} />
                                <InfoRow label="Phone" value={info.phone} />
                            </>
                        )}

                        <div className="pt-3 mt-3 border-t border-gray-100">
                            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <MapPin className="w-3 h-3" />
                                Mailing Address
                            </h4>
                            <div className="space-y-3">
                                {editingCard === 'contact' ? (
                                    <>
                                        <EditableField label="Street 1" name="mailingStreet1" value={form.mailingStreet1} onChange={handleChange} error={fieldErrors.mailingStreet1} />
                                        <EditableField label="Street 2" name="mailingStreet2" value={form.mailingStreet2} onChange={handleChange} error={fieldErrors.mailingStreet2} />
                                        <EditableField label="City" name="mailingCity" value={form.mailingCity} onChange={handleChange} error={fieldErrors.mailingCity} />
                                        <EditableField label="Province" name="mailingProv" value={form.mailingProv} onChange={handleChange} error={fieldErrors.mailingProv} />
                                        <EditableField label="Postal Code" name="mailingPostalCode" value={form.mailingPostalCode} onChange={handleChange} error={fieldErrors.mailingPostalCode} />
                                    </>
                                ) : (
                                    <>
                                        <InfoRow label="Street 1" value={info.mailingStreet1} />
                                        <InfoRow label="Street 2" value={info.mailingStreet2} />
                                        <InfoRow label="City" value={info.mailingCity} />
                                        <InfoRow label="Province" value={info.mailingProv} />
                                        <InfoRow label="Postal Code" value={info.mailingPostalCode} />
                                    </>
                                )}
                                <InfoRow label="Country" value={info.mailingCountry} />
                            </div>
                        </div>
                    </div>
                </div>

                {!clientOnly && (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                            <div className="p-2 bg-primary/10 rounded-lg">
                                <Palette className="w-5 h-5 text-primary" />
                            </div>
                            <h3 className="font-semibold text-gray-900">Branding</h3>
                        </div>
                        <div className="p-5">
                            <FileUpload />
                        </div>
                    </div>
                )}

                {!clientOnly && <AgreementSection />}
            </div>

            <AccountSwitcherModal
                isOpen={showSwitchModal}
                onClose={() => setShowSwitchModal(false)}
            />
        </div>
    );
};

export default AccountInfoOwner;
